import { useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowDown } from "lucide-react";
import { IMAGES } from "../data/content";
import { Reveal } from "../components/Reveal";
import { scrollToTarget } from "../lib/scroll";

const Transformations = () => {
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const hovering = useRef(false);

  const cardStep = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 300;
    const card = track.querySelector("figure");
    return card ? card.offsetWidth + 20 : 300;
  }, []);

  const prev = useCallback(() => {
    trackRef.current?.scrollBy({ left: -cardStep(), behavior: "smooth" });
  }, [cardStep]);

  const next = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 10;
    if (atEnd) {
      track.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      track.scrollBy({ left: cardStep(), behavior: "smooth" });
    }
  }, [cardStep]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!drag.current.down && !hovering.current) next();
    }, 4500);
    return () => clearInterval(id);
  }, [next]);

  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    const track = trackRef.current;
    if (!track) return;
    e.preventDefault();
    const startX = e.clientX;
    const startScroll = track.scrollLeft;
    track.style.scrollSnapType = "none";
    hovering.current = true;
    drag.current.moved = false;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      if (Math.abs(dx) > 6) drag.current.moved = true;
      track.scrollLeft = startScroll - dx;
    };
    const onUp = () => {
      track.style.scrollSnapType = "";
      hovering.current = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <section id="transformaciones" data-testid="rnb-transformations-section" className="py-20 lg:py-32 bg-[#050505] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.4em] text-zinc-500 uppercase">Antes / Después</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              TRANSFORMACIONES REALES
            </h2>
            <p className="mt-4 text-base sm:text-lg text-zinc-400 font-light max-w-xl">
              Resultados que reflejan el poder de hacer las cosas bien.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-[10px] tracking-[0.3em] text-zinc-600 uppercase">Arrastra o desliza</span>
            <div className="flex items-center gap-3">
              <button
                onClick={prev}
                data-testid="rnb-transf-prev"
                aria-label="Anterior transformación"
                className="h-12 w-12 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:bg-white hover:text-black hover:border-white transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={next}
                data-testid="rnb-transf-next"
                aria-label="Siguiente transformación"
                className="h-12 w-12 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:bg-white hover:text-black hover:border-white transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15} className="mt-12">
        <div
          ref={trackRef}
          data-testid="rnb-transf-track"
          data-lenis-prevent
          onPointerDown={onPointerDown}
          onClickCapture={onClickCapture}
          onMouseEnter={() => (hovering.current = true)}
          onMouseLeave={() => (hovering.current = false)}
          className="no-scrollbar flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory pl-4 sm:pl-6 lg:pl-8 pr-4 sm:pr-6 lg:pr-8 cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: "pan-y pinch-zoom" }}
        >
          {IMAGES.transformations.map((t, i) => (
            <figure
              key={t.src}
              data-testid={`rnb-transformation-card-${i + 1}`}
              className="group min-w-0 shrink-0 grow-0 basis-[68%] sm:basis-[40%] lg:basis-[24%] snap-start"
            >
              <div className="relative overflow-hidden border border-zinc-800 bg-[#0D0D0D]">
                <img
                  src={t.src}
                  alt={`Transformación real: ${t.label}`}
                  loading="lazy"
                  draggable={false}
                  className="w-full h-auto pointer-events-none transition-transform duration-700 group-hover:scale-[1.02]"
                />
                {i === 0 && (
                  <span className="absolute top-3 left-3 bg-white text-black text-[9px] font-bold tracking-[0.25em] px-3 py-1.5">
                    DANI POSTIGO
                  </span>
                )}
              </div>
              <figcaption className="mt-3 flex items-baseline justify-between gap-3">
                <span className="font-display text-base tracking-wider text-white">{t.label}</span>
                <span className="text-[9px] tracking-[0.25em] text-zinc-500 uppercase text-right">{t.tag}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.2} className="mt-12 text-center">
        <button
          onClick={() => scrollToTarget("#coaching-vip")}
          data-testid="rnb-transf-cta"
          className="group inline-flex items-center gap-3 bg-white text-black font-display text-xl tracking-[0.15em] px-8 py-4 hover:bg-zinc-200 transition-colors"
        >
          QUIERO MI TRANSFORMACIÓN
          <ArrowDown className="h-5 w-5 transition-transform duration-300 group-hover:translate-y-1" />
        </button>
      </Reveal>
    </section>
  );
};

export default Transformations;
